import rafSchd from 'raf-schd';
import React from 'react';
import { getBoundingClientObj } from 'react-select/src/utils';
import { CSSTransition } from 'react-transition-group';
import { ExitHandler } from 'react-transition-group/Transition';
import useResizeObserver from 'use-resize-observer';
import Portal from '../../../../portal';
import {
  IAddConfig,
  ICalendarEvent,
  IMenuRenderInfo,
  ISnapConfig,
  TEventType,
} from '../../../calendars';
import {
  calculateMenuAbsolutePosition,
  getBoundingClientRectObj,
  whereToOpen,
} from '../../week-utils';
import { EventMenuSC } from './event-menu.styled';

const DEFAULT_SNAP = {
  top: undefined,
  bottom: undefined,
};

interface EventMenuProps<T> {
  visible: boolean;
  eventRect?: ReturnType<typeof getBoundingClientObj>;
  onClose: () => void;
  onExited?: ExitHandler<any>;
  style?: React.CSSProperties;
  renderMenu: (info: IMenuRenderInfo<T>) => React.ReactElement;
  event?: ICalendarEvent<T>;
  eventType?: TEventType;
  currentElementRef?: React.RefObject<any>;
  menuWidth: number;
  onEdit: (config: IAddConfig<T>) => void;
  timeLineWidth: number;
  snapMenu?: ISnapConfig;
  calendarEventsRef: React.RefObject<HTMLDivElement>;
  calendarWrapperRef?: React.RefObject<HTMLDivElement>;
}

const EventMenu = <T extends any>({
  renderMenu,
  visible,
  style,
  onClose,
  onExited,
  event,
  eventType,
  currentElementRef,
  menuWidth,
  onEdit,
  timeLineWidth,
  snapMenu = DEFAULT_SNAP,
  calendarEventsRef,
  calendarWrapperRef,
}: EventMenuProps<T>) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [forceRender, setRender] = React.useState(0);
  const [, forceRenderMenu] = React.useState({});
  useResizeObserver<HTMLDivElement>({ ref: contentRef });
  const [eventRect, setEventRect] = React.useState<DOMRect | undefined>();

  const handleMenuPos = React.useCallback(() => {
    const newPosition = getBoundingClientRectObj(
      currentElementRef?.current?.getBoundingClientRect(),
    );
    setEventRect((o) => (newPosition || o) as DOMRect);
  }, [currentElementRef]);

  React.useLayoutEffect(() => {
    const callback = rafSchd(handleMenuPos);
    const node = calendarEventsRef.current;

    if (visible) {
      node?.addEventListener('scroll', callback);
      window.addEventListener('scroll', callback);
    }

    return () => {
      window.removeEventListener('scroll', callback);
      node?.removeEventListener('scroll', callback);
    };
  }, [calendarEventsRef, currentElementRef, visible, handleMenuPos]);

  React.useEffect(() => {
    if (visible) {
      handleMenuPos();
    }
  }, [visible, timeLineWidth, forceRender, event, handleMenuPos]);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // @ts-ignore
      if (
        wrapperRef.current &&
        // @ts-ignore
        !wrapperRef.current?.contains(event.target) &&
        // @ts-ignore
        !currentElementRef?.current?.contains(event.target)
      ) {
        onClose?.();
      }
    }

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [currentElementRef, wrapperRef, onClose, visible]);

  React.useEffect(() => {
    function handleEsc(event: React.KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose?.();
      }
    }
    if (visible) {
      // @ts-ignore
      document.addEventListener('keydown', handleEsc, false);
    }
    return () => {
      // @ts-ignore
      document.removeEventListener('keydown', handleEsc, false);
    };
  }, [onClose, visible]);

  React.useEffect(() => {
    const handleResize = rafSchd(() => {
      forceRenderMenu({});
    });

    if (visible) {
      window.addEventListener('resize', handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [visible]);

  React.useEffect(() => {
    if (!visible) {
      setEventRect(undefined);
      setRender(0);
    }
  }, [visible]);

  const menuDims = wrapperRef.current?.getBoundingClientRect();
  const position = whereToOpen(eventRect as any, menuDims, menuWidth);
  const { top, left } = calculateMenuAbsolutePosition({
    menuDims,
    position,
    eventDims: eventRect,
    snapPoints: snapMenu,
    menuWidth,
    wrapperRect: calendarWrapperRef?.current?.getBoundingClientRect(),
  });

  return (
    <Portal style={{ background: 'transparent' }}>
      <CSSTransition
        in={visible}
        timeout={300}
        classNames="week-calendar-menu"
        unmountOnExit
        onExited={onExited}
        onEntered={() => setRender(1)}
      >
        <EventMenuSC
          ref={wrapperRef}
          menuWidth={menuWidth}
          verticalPosition={position?.verticalPosition || 'down'}
          style={{
            ...style,
            top,
            left,
          }}
        >
          <div style={{ height: 'min-content', width: '100%' }} ref={contentRef}>
            {visible && event
              ? renderMenu({ event, direction: position, onClose, type: eventType!, onEdit })
              : null}
          </div>
        </EventMenuSC>
      </CSSTransition>
    </Portal>
  );
};

export default React.memo(EventMenu) as typeof EventMenu;
